import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { authService } from '@/services/auth.service';
import { analyticsService } from '@/services/analytics.service';
import { QUERY_KEYS } from '@/utils/constants';
import { clearUiState } from '@/utils/persistentUiState';
import type { AuthUser, LoginRequest } from '@/types/auth';

const ME_KEY = [QUERY_KEYS.AUTH_ME] as const;

/** 프로필을 다시 받아 오기까지 기다리는 시간. 라우트를 옮길 때마다 부르지 않게 한다 */
const PROFILE_STALE_TIME = 5 * 60_000;

/**
 * 저장된 토큰으로 `GET /auth/me` 를 받아 스토어를 채운다.
 *
 * 새로고침 직후에는 저장소 마이그레이션(v2)이 프로필을 비우고 토큰만 남기므로,
 * 이걸 부르지 않으면 사이드바·권한이 빈 채로 그려진다. 라우트 가드가 마운트될 때
 * 한 번 돌게 배선해 두었고, React Query 캐시가 중복 호출을 막는다.
 */
export const useProfileSync = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setUser = useAuthStore((state) => state.setUser);

  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: authService.me,
    enabled: isAuthenticated,
    staleTime: PROFILE_STALE_TIME,
    // 실패해도 재시도하지 않는다. 401 이면 인터셉터가 이미 로그인으로 보낸다
    retry: false,
  });

  const { data } = query;

  useEffect(() => {
    if (data) setUser(data);
  }, [data, setUser]);

  return query;
};

export const useAuth = () => {
  const {
    isAuthenticated,
    username,
    displayName,
    role,
    status,
    institutionId,
    institutionName,
    allowedTabs,
    setAuth,
    setUser,
    clearAuth,
  } = useAuthStore();
  const queryClient = useQueryClient();

  /**
   * 로그인. 토큰에서 role 을 추측하지 않고 응답의 `user` 객체를 그대로 쓴다 —
   * 기관명·탭 권한처럼 토큰에 없는 값이 화면 분기의 근거이기 때문이다(계약 §2).
   */
  const login = useCallback(
    async (credentials: LoginRequest): Promise<AuthUser> => {
      const { accessToken, user } = await authService.login(credentials);
      setAuth(accessToken, user);
      // 방금 받은 프로필로 캐시를 채워 착지 직후 `/auth/me` 를 한 번 더 부르지 않게 한다
      queryClient.setQueryData(ME_KEY, user);
      analyticsService.trackEvent({ eventType: 'LOGIN', isGuest: false }).catch(() => {});
      return user;
    },
    [queryClient, setAuth],
  );

  const logout = useCallback(() => {
    // axios 요청 인터셉터는 프로미스 체인이라 다음 줄(clearAuth)이 끝난 *뒤*
    // 마이크로태스크로 돈다 — 호출 순서를 이렇게 적어도 인터셉터가 토큰을 읽을 땐
    // 이미 지워진 뒤였고, 그래서 모든 LOGOUT 이벤트가 userId 없이 저장됐다.
    // 요청이 끝난 뒤에 지우면 인터셉터가 토큰을 읽는 시점엔 아직 남아 있다.
    // 세션 닫기도 같은 이유로 토큰을 지우기 전에 보낸다. 실패해도(이미 끊긴 세션 등) 로그아웃은 한다
    Promise.allSettled([
      analyticsService.trackEvent({ eventType: 'LOGOUT' }),
      authService.logout(),
    ])
      .finally(() => {
        clearAuth();
        // 다음 계정이 이전 계정의 목록·통계를 잠깐이라도 보지 않도록 캐시를 비운다
        queryClient.clear();
        // 고른 기관·검색어 같은 화면 기억도 함께 지운다 (`@/utils/persistentUiState`)
        clearUiState();
      });
  }, [clearAuth, queryClient]);

  /** `GET /auth/me` 로 프로필을 갱신한다. 권한을 바꾼 직후 등에 직접 부른다 */
  const refreshProfile = useCallback(async (): Promise<AuthUser> => {
    const user = await queryClient.fetchQuery({
      queryKey: ME_KEY,
      queryFn: authService.me,
      staleTime: 0,
    });
    setUser(user);
    return user;
  }, [queryClient, setUser]);

  return {
    isAuthenticated,
    username,
    displayName,
    role,
    status,
    institutionId,
    institutionName,
    allowedTabs,
    /** 최고 관리자. 기능별 판단은 `useAdminAccess()` 를 쓴다 */
    isAdmin: role === 'admin',
    isInstitution: role === 'institution',
    login,
    logout,
    refreshProfile,
  };
};
